import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Pencil, Plus, Lock, LockOpen, X, Upload, Loader2, ImagePlus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { nom: "", lieu: "", dates: "", description: "", programmeText: "", affiche: "", images: [], inscriptions_ouvertes: false, ordre: 0 };

function toForm(d) {
  return {
    nom: d.nom, lieu: d.lieu || "", dates: d.dates || "", description: d.description || "",
    programmeText: (d.programme || []).map((p) => `${p.heure} | ${p.titre}`).join("\n"),
    affiche: d.affiche || "", images: d.images || [],
    inscriptions_ouvertes: d.inscriptions_ouvertes, ordre: d.ordre || 0,
    categories_fermees: d.categories_fermees || [], capacites: d.capacites || null,
    quota_mode: d.quota_mode || "categories", capacites_genres: d.capacites_genres || null,
  };
}

const CATS = [["u19m", "U19 Masculin"], ["u23m", "U23 Masculin"], ["u19f", "U19 Féminin"], ["u23f", "U23 Féminin"]];
const GENRES = [["masculin", "Hommes (U19 + U23)"], ["féminin", "Femmes (U19 + U23)"]];

export default function AdminDetections() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(null);

  const uploadImage = async (file, target) => {
    if (!file) return;
    setUploading(target);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/admin/upload`, fd, { withCredentials: true });
      if (target === "affiche") {
        setForm((f) => ({ ...f, affiche: data.url }));
      } else {
        setForm((f) => ({ ...f, images: [...f.images, data.url] }));
      }
      toast.success("Image téléversée");
    } catch {
      toast.error("Échec du téléversement");
    } finally {
      setUploading(null);
    }
  };

  const load = useCallback(() => {
    axios.get(`${API}/detections`).then((r) => setItems(r.data)).catch(() => toast.error("Erreur de chargement"));
  }, []);
  useEffect(load, [load]);

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      nom: form.nom, lieu: form.lieu, dates: form.dates, description: form.description,
      programme: form.programmeText.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
        const [heure, ...rest] = l.split("|").map((s) => (s || "").trim());
        return { heure, titre: rest.join(" | ") };
      }).filter((p) => p.heure && p.titre),
      affiche: form.affiche || null, images: form.images,
      inscriptions_ouvertes: form.inscriptions_ouvertes, ordre: +form.ordre || 0,
      categories_fermees: form.categories_fermees || [], capacites: form.capacites || null,
      quota_mode: form.quota_mode || "categories", capacites_genres: form.capacites_genres || null,
    };
    try {
      if (editing) {
        await axios.put(`${API}/admin/detections/${editing}`, payload, { withCredentials: true });
        toast.success("Détection mise à jour");
      } else {
        await axios.post(`${API}/admin/detections`, payload, { withCredentials: true });
        toast.success("Détection créée — un onglet est apparu sur la page Détection");
      }
      setForm(empty); setEditing(null); setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const toggleOpen = async (d) => {
    try {
      const payload = toForm(d);
      await axios.put(`${API}/admin/detections/${d.id}`, {
        nom: payload.nom, lieu: payload.lieu, dates: payload.dates, description: payload.description,
        programme: d.programme || [], affiche: d.affiche || null, images: d.images || [],
        inscriptions_ouvertes: !d.inscriptions_ouvertes, ordre: d.ordre || 0,
        categories_fermees: d.categories_fermees || [], capacites: d.capacites || null,
        quota_mode: d.quota_mode || "categories", capacites_genres: d.capacites_genres || null,
      }, { withCredentials: true });
      toast.success(d.inscriptions_ouvertes ? `Inscriptions fermées pour ${d.nom}` : `Inscriptions ouvertes pour ${d.nom}`);
      load();
    } catch {
      toast.error("Erreur");
    }
  };

  const toggleCategorie = async (d, key) => {
    const fermees = d.categories_fermees || [];
    const next = fermees.includes(key) ? fermees.filter((k) => k !== key) : [...fermees, key];
    const label = CATS.find(([k]) => k === key)[1];
    try {
      await axios.put(`${API}/admin/detections/${d.id}`, {
        nom: d.nom, lieu: d.lieu || "", dates: d.dates || "", description: d.description || "",
        programme: d.programme || [], affiche: d.affiche || null, images: d.images || [],
        inscriptions_ouvertes: d.inscriptions_ouvertes, ordre: d.ordre || 0,
        categories_fermees: next, capacites: d.capacites || null,
        quota_mode: d.quota_mode || "categories", capacites_genres: d.capacites_genres || null,
      }, { withCredentials: true });
      toast.success(fermees.includes(key) ? `Inscriptions ${label} rouvertes` : `Inscriptions ${label} closes`);
      load();
    } catch {
      toast.error("Erreur");
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Supprimer la détection « ${d.nom} » ?`)) return;
    try {
      await axios.delete(`${API}/admin/detections/${d.id}`, { withCredentials: true });
      toast.success("Détection supprimée");
      if (editing === d.id) { setEditing(null); setForm(empty); setShowForm(false); }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const setQuotaMode = async (d, mode) => {
    try {
      await axios.put(`${API}/admin/detections/${d.id}`, {
        nom: d.nom, lieu: d.lieu || "", dates: d.dates || "", description: d.description || "",
        programme: d.programme || [], affiche: d.affiche || null, images: d.images || [],
        inscriptions_ouvertes: d.inscriptions_ouvertes, ordre: d.ordre || 0,
        categories_fermees: d.categories_fermees || [], capacites: d.capacites || null,
        quota_mode: mode, capacites_genres: d.capacites_genres || null,
      }, { withCredentials: true });
      toast.success(mode === "genres" ? "Option 2 activée — quotas globaux : 180 hommes / 90 femmes" : "Option 1 activée — quotas par catégorie");
      load();
    } catch {
      toast.error("Erreur");
    }
  };

  return (
    <div data-testid="admin-detections">
      <div className="flex items-center justify-between mb-8">
        <p className="text-sm text-[#71717A]">Chaque détection devient un onglet sur la page Détection du site. Ouvrez ou fermez les inscriptions d'un clic.</p>
        <button
          onClick={() => { setShowForm(!showForm); setEditing(null); setForm(empty); }}
          data-testid="detection-new-button"
          className="flex items-center gap-2 shrink-0 bg-[#C5A059] text-black px-6 py-3 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#E5C07B] transition-colors"
        >
          {showForm ? <X size={14} /> : <Plus size={14} />} {showForm ? "Fermer" : "Nouvelle détection"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={save} className="border border-[#27272A] bg-[#121214] p-6 mb-10" data-testid="detection-form">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-5">{editing ? "Modifier la détection" : "Nouvelle détection"}</p>
          <div className="grid sm:grid-cols-2 gap-5">
            <input required placeholder="Nom * (ex : Lubumbashi — Mars 2027)" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} data-testid="detection-input-nom"
              className="bg-[#0A0A0C] border border-[#27272A] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059]" />
            <input placeholder="Dates (ex : 5 – 9 Mars 2027)" value={form.dates} onChange={(e) => setForm({ ...form, dates: e.target.value })} data-testid="detection-input-dates"
              className="bg-[#0A0A0C] border border-[#27272A] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059]" />
            <input placeholder="Lieu (ex : Lubumbashi, RDC)" value={form.lieu} onChange={(e) => setForm({ ...form, lieu: e.target.value })} data-testid="detection-input-lieu"
              className="bg-[#0A0A0C] border border-[#27272A] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059]" />
            <input type="number" min="0" placeholder="Ordre d'affichage" value={form.ordre} onChange={(e) => setForm({ ...form, ordre: e.target.value })} data-testid="detection-input-ordre"
              className="bg-[#0A0A0C] border border-[#27272A] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059]" />
          </div>
          <textarea rows={3} placeholder="Description affichée sous le titre de l'onglet" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="detection-input-description"
            className="w-full mt-5 bg-[#0A0A0C] border border-[#27272A] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059] resize-none" />
          <textarea rows={5} placeholder={"Programme — une ligne par étape, format « Horaire | Étape »\nJeu 5 Mar | Accueil et tests physiques\nVen 6 Mar | Matchs de détection"} value={form.programmeText} onChange={(e) => setForm({ ...form, programmeText: e.target.value })} data-testid="detection-input-programme"
            className="w-full mt-5 bg-[#0A0A0C] border border-[#27272A] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059] resize-none font-mono" />
          <div className="grid sm:grid-cols-2 gap-5 mt-5">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-[#71717A] mb-2">Affiche de la détection (grande image en haut de l'onglet)</p>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 border border-[#27272A] px-4 py-2.5 text-[10px] uppercase tracking-widest text-[#A1A1AA] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors cursor-pointer" data-testid="detection-affiche-button">
                  {uploading === "affiche" ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                  {form.affiche ? "Changer l'affiche" : "Téléverser l'affiche"}
                  <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="detection-affiche-input" onChange={(e) => uploadImage(e.target.files[0], "affiche")} />
                </label>
                {form.affiche && <img src={form.affiche} alt="Affiche" className="h-14 w-auto border border-[#C5A059]/50" data-testid="detection-affiche-preview" />}
              </div>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-[#71717A] mb-2">Images supplémentaires (galerie)</p>
              <div className="flex flex-wrap items-center gap-3">
                {form.images.map((img, i) => (
                  <div key={i} className="relative group" data-testid={`detection-image-${i}`}>
                    <img src={img} alt={`Visuel ${i + 1}`} className="h-14 w-auto border border-[#27272A]" />
                    <button type="button" onClick={() => setForm((f) => ({ ...f, images: f.images.filter((_, j) => j !== i) }))} data-testid={`detection-image-remove-${i}`}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Retirer"><X size={11} /></button>
                  </div>
                ))}
                <label className="flex items-center gap-2 border border-dashed border-[#27272A] px-4 py-2.5 text-[10px] uppercase tracking-widest text-[#71717A] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors cursor-pointer" data-testid="detection-image-add">
                  {uploading === "gallery" ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />} Ajouter
                  <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="detection-image-input" onChange={(e) => uploadImage(e.target.files[0], "gallery")} />
                </label>
              </div>
            </div>
          </div>
          <label className="flex items-center gap-3 mt-5 cursor-pointer">
            <input type="checkbox" checked={form.inscriptions_ouvertes} onChange={(e) => setForm({ ...form, inscriptions_ouvertes: e.target.checked })} data-testid="detection-input-open" className="h-4 w-4 accent-[#C5A059]" />
            <span className="text-sm text-[#A1A1AA]">Inscriptions ouvertes dès la création</span>
          </label>
          <button type="submit" data-testid="detection-save-button" className="mt-6 bg-[#C5A059] text-black px-8 py-3 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#E5C07B] transition-colors">
            {editing ? "Mettre à jour" : "Créer la détection"}
          </button>
        </form>
      )}

      <div className="space-y-4">
        {items.map((d) => (
          <div key={d.id} data-testid={`detection-row-${d.id}`} className="border border-[#27272A] bg-[#121214] p-5 flex flex-wrap items-center gap-x-8 gap-y-4">
            <div className="flex-1 min-w-[220px]">
              <p className="font-display uppercase font-bold">{d.nom}</p>
              <p className="text-xs text-[#71717A] mt-1">{[d.dates, d.lieu].filter(Boolean).join(" · ") || "—"}</p>
            </div>
            <span className={`text-[10px] uppercase tracking-widest px-3 py-1.5 border ${d.inscriptions_ouvertes ? "border-[#C5A059]/60 text-[#C5A059]" : "border-[#3f3f46] text-[#71717A]"}`} data-testid={`detection-status-${d.id}`}>
              {d.inscriptions_ouvertes ? "Inscriptions ouvertes" : "Inscriptions fermées"}
            </span>
            <div className="flex items-center gap-3">
              <button onClick={() => toggleOpen(d)} data-testid={`detection-toggle-${d.id}`}
                className={`flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-[0.15em] border transition-colors ${d.inscriptions_ouvertes ? "border-[#3f3f46] text-[#A1A1AA] hover:border-red-400 hover:text-red-400" : "border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059] hover:text-black"}`}>
                {d.inscriptions_ouvertes ? <><Lock size={13} /> Fermer</> : <><LockOpen size={13} /> Ouvrir</>}
              </button>
              <button onClick={() => { setEditing(d.id); setForm(toForm(d)); setShowForm(true); window.scrollTo({ top: 0, behavior: "smooth" }); }} data-testid={`detection-edit-${d.id}`}
                className="text-[#71717A] hover:text-[#C5A059] transition-colors" aria-label="Modifier"><Pencil size={15} /></button>
              {d.id !== "kinshasa-2026" && (
                <button onClick={() => remove(d)} data-testid={`detection-delete-${d.id}`} className="text-[#71717A] hover:text-red-400 transition-colors" aria-label="Supprimer"><Trash2 size={15} /></button>
              )}
            </div>
            {/* Quotas d'inscrits : option 1 (par catégorie) ou option 2 (totaux par genre) */}
            <div className="w-full mt-4 border-t border-[#27272A] pt-4">
              <div className="flex flex-wrap items-center gap-2 mb-3" data-testid={`detection-quota-mode-${d.id}`}>
                <span className="text-[10px] uppercase tracking-widest text-[#71717A] mr-2">Nombre d'inscrits :</span>
                <button
                  onClick={() => setQuotaMode(d, "categories")}
                  data-testid={`quota-mode-categories-${d.id}`}
                  className={`text-[10px] uppercase tracking-wider px-3 py-1.5 border transition-colors ${(d.quota_mode || "categories") === "categories" ? "bg-[#C5A059] text-black border-[#C5A059] font-semibold" : "border-[#27272A] text-[#71717A] hover:border-[#C5A059] hover:text-[#C5A059]"}`}
                >
                  Option 1 — Par catégorie (528 · 396 · 132 · 132)
                </button>
                <button
                  onClick={() => setQuotaMode(d, "genres")}
                  data-testid={`quota-mode-genres-${d.id}`}
                  className={`text-[10px] uppercase tracking-wider px-3 py-1.5 border transition-colors ${d.quota_mode === "genres" ? "bg-[#C5A059] text-black border-[#C5A059] font-semibold" : "border-[#27272A] text-[#71717A] hover:border-[#C5A059] hover:text-[#C5A059]"}`}
                >
                  Option 2 — 180 hommes · 90 femmes
                </button>
                {(d.quota_mode || "categories") === "genres" && (
                  <span className="text-[9px] text-[#71717A] italic">Fermeture automatique des inscriptions dès le quota du genre atteint.</span>
                )}
              </div>

              {d.quota_mode === "genres" ? (
                <div className="grid sm:grid-cols-2 gap-3" data-testid={`detection-capacites-${d.id}`}>
                  {GENRES.map(([g, label]) => {
                    const n = (d.compteurs_genres || {})[g] || 0;
                    const cap = (d.capacites_genres || {})[g] ?? 0;
                    const pct = cap ? Math.min(100, Math.round((n / cap) * 100)) : 0;
                    const complete = cap > 0 && n >= cap;
                    return (
                      <div key={g} className={`border p-3.5 ${complete ? "border-[#C5A059]" : "border-[#27272A]"}`} data-testid={`detection-genre-${g}-${d.id}`}>
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-[10px] uppercase tracking-widest text-[#A1A1AA]">{label}</p>
                          {complete && <span className="text-[8px] uppercase tracking-wider px-1.5 py-0.5 bg-[#C5A059] text-black font-bold" data-testid={`detection-genre-full-${g}-${d.id}`}>Complet — inscriptions closes</span>}
                        </div>
                        <p className="text-white font-display font-bold text-lg mt-1">{n}<span className="text-[#71717A] text-xs font-normal">/{cap}</span></p>
                        <div className="h-1 bg-[#27272A] mt-2 relative">
                          <div className={`absolute inset-y-0 left-0 ${complete ? "bg-[#C5A059]" : "bg-[#71717A]"}`} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid={`detection-capacites-${d.id}`}>
                  {CATS.map(([key, label]) => {
                    const n = (d.compteurs || {})[key] || 0;
                    const cap = (d.capacites || {})[key] ?? 0;
                    const pct = cap ? Math.min(100, Math.round((n / cap) * 100)) : 0;
                    const complete = cap > 0 && n >= cap;
                    return (
                      <div key={key} className={`border p-3.5 ${complete ? "border-[#C5A059]" : "border-[#27272A]"}`} data-testid={`detection-cat-${key}-${d.id}`}>
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-[10px] uppercase tracking-widest text-[#A1A1AA]">{label}</p>
                          {complete && <span className="text-[8px] uppercase tracking-wider px-1.5 py-0.5 bg-[#C5A059] text-black font-bold" data-testid={`detection-cat-full-${key}-${d.id}`}>Complète</span>}
                        </div>
                        <p className="text-white font-display font-bold text-lg mt-1">{n}<span className="text-[#71717A] text-xs font-normal">/{cap}</span></p>
                        <div className="h-1 bg-[#27272A] mt-2 relative">
                          <div className={`absolute inset-y-0 left-0 ${complete ? "bg-[#C5A059]" : "bg-[#71717A]"}`} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Fermeture manuelle par catégorie (dans les deux options) */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
                {CATS.map(([key, label]) => {
                  const fermee = (d.categories_fermees || []).includes(key);
                  return (
                    <button
                      key={key}
                      onClick={() => toggleCategorie(d, key)}
                      data-testid={`detection-cat-toggle-${key}-${d.id}`}
                      className={`text-[9px] uppercase tracking-wider px-2 py-1.5 border transition-colors ${fermee ? "border-red-400/60 text-red-400" : "border-[#27272A] text-[#71717A] hover:border-[#C5A059] hover:text-[#C5A059]"}`}
                    >
                      {label} — {fermee ? "inscriptions closes · rouvrir" : "fermer les inscriptions"}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
